import React from "react";
import DatePicker from "react-datepicker";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { ThunkDispatch } from "redux-thunk";

import {
  PlanTypeRealEstateType,
  RealEstateType,
  PREmailAvailType,
  PlanTypeDisplayOrder,
} from "../../constants";
import { formatDateDtAPIPost } from "../../libs";
import { editingAccountActions } from "../../reducers/editingAccount";
import { DropdownIcon, ShowPassword, HidePassword } from "../SVGIcons";

interface EditingAccountState {
  editingPage: "create" | "update";
  email: string;
  userName: string;
  password: string;
  showPassword: boolean;
  planType: string;
  creditLimit: string;
  corporateName: string;
  storeName: string;
  picName: string;
  postCode: string;
  address: string;
  contractStart: Date | null;
  contractEnd: Date | null;
  prEmailAvail: string;
  registrationRoute: string;
  remarks: string;
  realEstateType: string;
  errors: Record<string, string>;
}

function mapStateToProps(state: { editingAccount: EditingAccountState }) {
  return { ...state.editingAccount };
}

interface AccountEditFormProps {
  editingPage: "create" | "update";
  email: string;
  userName: string;
  password: string;
  showPassword: boolean;
  planType: string;
  creditLimit: string;
  corporateName: string;
  storeName: string;
  picName: string;
  postCode: string;
  address: string;
  contractStart: Date | null;
  contractEnd: Date | null;
  prEmailAvail: string;
  registrationRoute: string;
  remarks: string;
  realEstateType: string;
  errors: Record<string, string>;
}

const AccountEditForm: React.FC<AccountEditFormProps> = ({
  editingPage,
  email,
  userName,
  password,
  showPassword,
  planType,
  creditLimit,
  corporateName,
  storeName,
  picName,
  postCode,
  address,
  contractStart,
  contractEnd,
  prEmailAvail,
  registrationRoute,
  remarks,
  realEstateType,
  errors,
}) => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, unknown>>();
  const navigate = useNavigate();

  const cancel = () => {
    navigate("/");
  };
  const submitForm = () => {
    dispatch(editingAccountActions.submitForm(navigate));
  };
  const switchShowPassword = () => {
    dispatch(editingAccountActions.switchShowPassword());
  };
  const setRealEstateType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(editingAccountActions.setRealEstateType(e.target.value));
    dispatch(editingAccountActions.setPlanType(""));
    dispatch(editingAccountActions.calcCreditLimit());
  };
  const setPlanType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(editingAccountActions.setPlanType(e.target.value));
    dispatch(editingAccountActions.calcCreditLimit());
  };
  const setContractStart = (date: Date | null) => {
    if (!date) dispatch(editingAccountActions.setContractStart(""));
    else
      dispatch(
        editingAccountActions.setContractStart(formatDateDtAPIPost(date)),
      );
  };
  const setContractEnd = (date: Date | null) => {
    if (!date) dispatch(editingAccountActions.setContractEnd(""));
    else
      dispatch(editingAccountActions.setContractEnd(formatDateDtAPIPost(date)));
  };

  return (
    <div className="container mx-auto my-3 pb-2 bg-white rounded-lg text-[#2d2d2d]">
      <h1 className="text-xl rounded-t-lg px-3 py-2 bg-[#5571ad] text-white">
        <b>{editingPage === "create" ? "アカウント登録" : "編集画面"}</b>
      </h1>
      <table className="min-w-full border-collapse bg-white">
        <colgroup>
          <col className="max-w-80 w-80" />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              法人名<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={corporateName}
                onChange={(e) =>
                  dispatch(
                    editingAccountActions.setCorporateName(e.target.value),
                  )
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.corporateName && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.corporateName}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              店舗名<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={storeName}
                onChange={(e) =>
                  dispatch(editingAccountActions.setStoreName(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.storeName && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.storeName}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              担当者氏名
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={picName}
                onChange={(e) =>
                  dispatch(editingAccountActions.setPICName(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.picName && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.picName}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              郵便番号
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={postCode}
                onChange={(e) =>
                  dispatch(editingAccountActions.setPostCode(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.postCode && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.postCode}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              住所
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={address}
                onChange={(e) =>
                  dispatch(editingAccountActions.setAddress(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.address && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.address}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              代表ユーザーID<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                value={userName}
                onChange={(e) =>
                  dispatch(editingAccountActions.setUserName(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.userName && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.userName}
                </p>
              )}
            </td>
          </tr>
          {editingPage === "create" && (
            <tr>
              <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
                パスワード<span className="text-red-600 ml-1">*</span>
              </th>
              <td className="border border-y-gray-300 p-3 relative">
                <div className="relative w-80">
                  <input
                    type={!showPassword ? "password" : ""}
                    value={password}
                    onChange={(e) =>
                      dispatch(
                        editingAccountActions.setPassword(e.target.value),
                      )
                    }
                    className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md pr-10 w-full"
                    autoComplete="off"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={switchShowPassword}
                    className="absolute top-[0.2rem] bottom-[0.2rem] right-[0.2rem] rounded-md flex items-center px-2 bg-white"
                  >
                    {!showPassword && <ShowPassword />}
                    {showPassword && <HidePassword />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">
                    <span className="font-medium">※</span>
                    {errors.password}
                  </p>
                )}
              </td>
            </tr>
          )}
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              代表メールアドレス<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                value={email}
                onChange={(e) =>
                  dispatch(editingAccountActions.setEmail(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.email}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              不動産種別<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="grid">
                <DropdownIcon />
                <select
                  value={realEstateType}
                  onChange={setRealEstateType}
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md appearance-none row-start-1 col-start-1"
                >
                  <option value="" />
                  {Object.keys(RealEstateType).map((key) => (
                    <option
                      key={key}
                      value={(RealEstateType as Record<string, string>)[key]}
                    >
                      {key}
                    </option>
                  ))}
                </select>
              </div>
              {errors.realEstateType && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.realEstateType}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              利用プラン<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="grid">
                <DropdownIcon />
                <select
                  value={planType}
                  onChange={setPlanType}
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md appearance-none row-start-1 col-start-1"
                >
                  <option value="" />
                  {(
                    PlanTypeRealEstateType as Record<
                      string,
                      Record<string, string>
                    >
                  )[realEstateType] &&
                    Object.keys(
                      (
                        PlanTypeRealEstateType as Record<
                          string,
                          Record<string, string>
                        >
                      )[realEstateType],
                    )
                      .sort((a, b) => {
                        // PlanTypeDisplayOrderに基づいてソート
                        return (
                          PlanTypeDisplayOrder.indexOf(a) -
                          PlanTypeDisplayOrder.indexOf(b)
                        );
                      })
                      .map((key) => (
                        <option
                          key={key}
                          value={
                            (
                              PlanTypeRealEstateType as Record<
                                string,
                                Record<string, string>
                              >
                            )[realEstateType][key]
                          }
                        >
                          {key}
                        </option>
                      ))}
                </select>
              </div>
              {errors.planType && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.planType}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              上限回数<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="relative">
                <input
                  value={creditLimit}
                  onChange={(e) =>
                    dispatch(
                      editingAccountActions.setCreditLimit(e.target.value),
                    )
                  }
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
                />
                {errors.creditLimit && (
                  <p className="mt-2 text-sm text-red-600">
                    <span className="font-medium">※</span>
                    {errors.creditLimit}
                  </p>
                )}
              </div>
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              契約開始日<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="relative">
                <DatePicker
                  dateFormat="yyyy/MM/dd"
                  selected={contractStart}
                  onChange={setContractStart}
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
                />
              </div>
              {errors.contractStart && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.contractStart}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              契約終了日
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="relative">
                <DatePicker
                  dateFormat="yyyy/MM/dd"
                  selected={contractEnd}
                  onChange={setContractEnd}
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
                />
              </div>
              {errors.contractEnd && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.contractEnd}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              販促メール<span className="text-red-600 ml-1">*</span>
            </th>
            <td className="border border-y-gray-300 p-3">
              <div className="grid">
                <DropdownIcon />
                <select
                  value={prEmailAvail}
                  onChange={(e) =>
                    dispatch(
                      editingAccountActions.setPrEmailAvail(e.target.value),
                    )
                  }
                  className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md appearance-none row-start-1 col-start-1"
                >
                  <option value="" />
                  {Object.keys(PREmailAvailType).map((key) => (
                    <option
                      key={key}
                      value={(PREmailAvailType as Record<string, string>)[key]}
                    >
                      {key}
                    </option>
                  ))}
                </select>
              </div>
              {errors.prEmailAvail && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.prEmailAvail}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              登録経路
            </th>
            <td className="border border-y-gray-300 p-3">
              <input
                type="text"
                value={registrationRoute}
                onChange={(e) =>
                  dispatch(
                    editingAccountActions.setRegistrationRoute(e.target.value),
                  )
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.registrationRoute && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.registrationRoute}
                </p>
              )}
            </td>
          </tr>
          <tr>
            <th className="bg-[#778ebe] border border-y-gray-200 px-3 py-2 text-white w-80">
              備考
            </th>
            <td className="border border-y-gray-300 p-3">
              <textarea
                rows={5}
                value={remarks}
                onChange={(e) =>
                  dispatch(editingAccountActions.setRemarks(e.target.value))
                }
                className="block w-full rounded-md border-0 p-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 text-md"
              />
              {errors.remarks && (
                <p className="mt-2 text-sm text-red-600">
                  <span className="font-medium">※</span>
                  {errors.remarks}
                </p>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex items-center justify-end gap-2 p-3 text-center">
        <button
          type="button"
          onClick={cancel}
          className="py-2.5 px-5 text-sm font-medium focus:outline-none bg-white rounded-lg border border-gray-300 hover:bg-gray-100 focus:z-10 focus:ring-4 focus:ring-gray-100 min-w-28"
        >
          戻る
        </button>
        <button
          type="button"
          onClick={submitForm}
          className="py-2.5 px-5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700  whitespace-nowrap min-w-28"
        >
          登録
        </button>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(AccountEditForm);
